import type { ComponentProps } from 'react';
import { asWidgetProps, SCROLL_TO_ELEMENT_DEFAULT } from 'Src/types/widget-helpers';
import React, { Fragment, useEffect, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { ceil, chunk, isEmpty, noop } from 'lodash';
import { Collapse } from 'react-collapse';
import { ICONS } from 'Components/utils/icon/icons';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { getCruiseExclusivity } from 'Components/widgets/product/Product.utils';
import type { PriceTicketContentProps } from 'Components/widgets/price-ticket/PriceTicketContentWrapper';
import 'Components/widgets/price-ticket/PriceTicketContentWrapper';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { BlockLink } from 'Components/frameworks/block-link/BlockLink';
import { Button } from 'Components/frameworks/button/Button';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Price } from 'Components/frameworks/price/Price';
import { NAVIGATION_OFFSET_SELECTOR } from 'Components/widgets/navigation/Navigation.constants';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { PriceRemoteDetailsToggle } from 'Components/widgets/price-remote/price-remote-details-toggle/PriceRemoteDetailsToggle';
import { PriceRemoteServiceList } from 'Components/widgets/price-remote/price-remote-service-list/PriceRemoteServiceList';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { Title } from 'Components/frameworks/title/Title';
type Props = PriceTicketContentProps & {
  isLite: boolean;
};
export const PriceTicketLarge = ({
  allInclusiveServices = [],
  button,
  className,
  fromPriceTicketCustom = false,
  hasCallCenter = false,
  id,
  isLite = false,
  isLoading = false,
  items = [],
  price,
  subtitle,
  title,
  travelAdvisorConfig,
}: Props) => {
  const [isDetailOpened, setIsDetailOpened] = useState(false);
  const { isSeller } = useAppContext();
  const track = useTracking();
  useEffect(() => {
    setIsDetailOpened(false);
  }, [isLite]);

  if (!title) {
    return null;
  }

  const openClosePriceDetails = () => {
    track({
      actionGA: 'priceBar',
      labelGA: isDetailOpened ? 'closeAllInclusive' : 'openAllInclusive',
    });
    setIsDetailOpened(!isDetailOpened);
  };

  const hasDetail = !isLite && !hasCallCenter && !isEmpty(items);
  const hasAllInclusiveServices = !!allInclusiveServices?.length;
  const scroll =
    button && button.url && /^(#|\.)/.test(button.url)
      ? () =>
          scrollToElement(
            button.url,
            500,
            `.${NAVIGATION_OFFSET_SELECTOR}`,
            SCROLL_TO_ELEMENT_DEFAULT,
          )
      : noop;
  const splitServices =
    allInclusiveServices && allInclusiveServices.length > 0
      ? chunk([...allInclusiveServices], ceil(allInclusiveServices.length / 2))
      : [];
  const { isCruiseExclusive, isCruiseExclusivityLoading } = getCruiseExclusivity(id);
  const exclusivityKey = isSeller ? 'booking.common.exclusivityb2b' : 'booking.common.exclusivity';
  return (
    <div
      className={classnames('animation-duration-750 relative isolate animate-fadeIn', className)}
      data-testid="PriceTicketLarge"
    >
      <SiteContainer isWide={isLite}>
        <div
          className={classnames(
            'relative isolate min-h-68 rounded-12 bg-white p-20 shadow-diffuse',
            {
              'border-1 border-pearl': !isLite,
              'border-t-1 border-lightGrey py-10': isLite,
              'flex items-center justify-center': isLoading,
            },
          )}
        >
          {isLoading || isCruiseExclusivityLoading ? (
            <Spinner className="text-black" size="small" />
          ) : (
            <Fragment>
              <div className={classnames('flex items-center justify-between gap-20')}>
                <Title
                  className="flex-1 flex-col !items-start"
                  dataTestId="priceTicket-title"
                  hLevel={1}
                  theme="title-sans-n1"
                >
                  <span className="text-16 text-sienna" data-testid="priceTicket-subtitle">
                    {subtitle}
                  </span>
                  {title}
                </Title>

                {!hasCallCenter && !isEmpty(price) && price.bestPrice && (
                  <Price
                    {...price}
                    className={classnames({
                      'items-end text-right': !isLite,
                    })}
                    from={t('price.format.from')}
                    hasInitialPrice={false}
                    hasTotalPrice
                    theme={isLite ? 'default' : 'column'}
                  />
                )}

                <div
                  className={classnames('flex items-center justify-end gap-20', {
                    'flex-1': isLite,
                  })}
                >
                  {hasAllInclusiveServices && isLite && (
                    <PriceRemoteDetailsToggle
                      handleClick={openClosePriceDetails}
                      isCollapsed={!isDetailOpened}
                    />
                  )}
                  {!isCruiseExclusive && hasCallCenter && travelAdvisorConfig && (
                    <BlockLink
                      className={classnames('text-black', {
                        hidden: isLite,
                      })}
                      icon={ICONS.PHONE_BUBBLE}
                      texts={travelAdvisorConfig.text}
                      url={travelAdvisorConfig.href}
                    />
                  )}
                  {!isCruiseExclusive && (fromPriceTicketCustom || isLite || hasCallCenter) && (
                    <Button
                      {...button}
                      className={classnames('self-center')}
                      size="small"
                      onClick={scroll}
                    />
                  )}
                  {isCruiseExclusive && (
                    <div
                      className="rounded-pill bg-orange px-15 py-10 text-12 font-bold text-white"
                      data-testid="exclusivity-label"
                    >
                      {t(exclusivityKey)}
                    </div>
                  )}
                </div>
              </div>

              {hasDetail && (
                <div className="flex items-center justify-between gap-20 pt-10">
                  <p className="w-auto font-sans text-16 font-bold text-black">
                    {t('productPriceTicketLine.startingPriceCriteriaLabel', {}, '')}
                  </p>
                  <div className="flex flex-1 items-center gap-20">
                    {items.map(({ iconName, label, longDesc, subDesc }) => (
                      <DynamicLink
                        className="flex items-center gap-10 font-sans text-14 text-black"
                        data-testid="PriceTicketLarge-item"
                        key={iconName}
                        url={button?.url}
                      >
                        <Icon
                          {...asWidgetProps<ComponentProps<typeof Icon>>({
                            className: 'shrink-0 text-sienna',
                            name: iconName,
                            title: label,
                            width: '1rem',
                          })}
                        />
                        {longDesc}
                        {subDesc && <span className="font-bold">{subDesc}</span>}
                      </DynamicLink>
                    ))}
                  </div>
                  <Button {...button} className="w-1/4" size="small" onClick={scroll} />
                </div>
              )}

              {hasAllInclusiveServices && isDetailOpened && (
                <Collapse
                  isOpened={isDetailOpened}
                  theme={{
                    content: 'pt-20',
                  }}
                >
                  <div className="flex gap-10">
                    {splitServices[0] && (
                      <PriceRemoteServiceList className="flex-1" services={splitServices[0]} />
                    )}
                    {splitServices[1] && (
                      <PriceRemoteServiceList className="flex-1" services={splitServices[1]} />
                    )}
                  </div>
                </Collapse>
              )}

              {hasAllInclusiveServices && !isLite && (
                <div className="mt-10 flex justify-end">
                  <PriceRemoteDetailsToggle
                    handleClick={openClosePriceDetails}
                    isCollapsed={!isDetailOpened}
                  />
                </div>
              )}
            </Fragment>
          )}
        </div>
      </SiteContainer>
    </div>
  );
};
